import type {
  DividerCdtLevel,
  DividerHardware,
  DividerPolicy,
  DividerPolicySource,
  DividersSetByHand,
  LoaderMode,
} from "@CommonTypes/backend/PspRules";
import type {
  PlanogramShelfDividers,
  ShelfLoaderBlockMode,
  ShelfLoaderDecision,
  ShelfLoaderDepthSource,
} from "@CommonTypes/merchflow/pog/shelf";
import type {
  ItemDividerNeed,
  ItemDividerReasonValue,
  ItemLoaderNeed,
} from "@CommonTypes/merchflow/pog/item";
import { getActiveShelfDividers } from "@CommonUtils/pog/shelfDividers";
import { normalizeLoaderDepth } from "@CommonUtils/pog/shelfLoader";

export type DividerAttributeValue = string | number | boolean | null | undefined;

/** A product as the divider policy reads it. */
export interface DividerPolicyProduct {
  productCode: string;
  cdt?: Partial<Record<DividerCdtLevel, string | null | undefined>>;
  attributes?: Readonly<Record<string, DividerAttributeValue>>;
}

interface DividerBaseItem {
  productCode: string;
  facings?: number | null;
}

interface DividerBaseShelf {
  type?: string;
  width?: number;
  depth?: number;
  notchNo?: number;
  dividers?: PlanogramShelfDividers | null;
  /** The base pog shelf's own (PSA) loader, x100. */
  loaderDepth?: number | null;
  items?: readonly DividerBaseItem[];
}

/** The base planogram as the policy reads it; a non-circular pog fits. */
export interface DividerBasePog {
  planogram?: { bays?: readonly { shelves?: readonly DividerBaseShelf[] }[] } | null;
}

export interface BaseDividerEvidence {
  /** Regular shelves holding at least one product. */
  shelves: number;
  dividerShelves: number;
  /** Distinct products in the base planogram. */
  products: number;
  /** Products with more than half of their facings on divider shelves. */
  productsNeedingDividers: number;
  /** productCode -> facings on divider shelves / all facings. */
  facingsByProduct: Map<string, { onDividerShelves: number; total: number }>;
  /** The most common trustworthy divider size, or null. */
  hardware: DividerHardware | null;
  /** Divider shelves whose width is not below the shelf's own width (an import fault). */
  widthNotBelowShelf: number;
  /** Divider shelves whose start/end switches hold numbers above 1 (an import fault). */
  flagsOutOfRange: number;
  /** The most common regular shelf width, or null without shelves. */
  typicalShelfWidth: number | null;
}

const toFiniteNumber = (value: unknown): number => {
  const number = Number(value ?? 0);
  return Number.isFinite(number) ? number : 0;
};

const flagOutOfRange = (dividers: PlanogramShelfDividers) =>
  [dividers.atStart, dividers.atEnd].some((flag) => toFiniteNumber(flag) > 1);

const mostCommon = <T>(values: readonly T[], keyOf: (value: T) => string): T | null => {
  const counts = new Map<string, { value: T; count: number }>();
  for (const value of values) {
    const key = keyOf(value);
    const entry = counts.get(key);
    if (entry) entry.count += 1;
    else counts.set(key, { value, count: 1 });
  }
  let best: { value: T; count: number } | null = null;
  for (const entry of counts.values()) {
    if (!best || entry.count > best.count) best = entry;
  }
  return best?.value ?? null;
};

const hardwareKey = (hardware: DividerHardware) =>
  [hardware.width, hardware.height, hardware.depth, hardware.atStart, hardware.atEnd].join("|");

/**
 * What the base planogram says about dividers. A shelf whose switches read
 * above 1 is not counted as a divider shelf at all; one whose divider is as
 * wide as the shelf still says which products sat behind dividers, but its
 * size is never offered as the default.
 */
export const getBaseDividerEvidence = (basePog: DividerBasePog | null | undefined) => {
  const facingsByProduct = new Map<string, { onDividerShelves: number; total: number }>();
  const trustedHardware: DividerHardware[] = [];
  const shelfWidths: number[] = [];
  let shelves = 0;
  let dividerShelves = 0;
  let widthNotBelowShelf = 0;
  let flagsOutOfRange = 0;

  for (const bay of basePog?.planogram?.bays ?? []) {
    for (const shelf of bay.shelves ?? []) {
      if (shelf.type && shelf.type !== "REGULAR") continue;
      const items = shelf.items ?? [];
      if (items.length === 0) continue;
      shelves += 1;
      const shelfWidth = toFiniteNumber(shelf.width);
      shelfWidths.push(shelfWidth);

      const active = getActiveShelfDividers(shelf.dividers);
      let hasDividers = false;
      if (active) {
        if (flagOutOfRange(active)) {
          flagsOutOfRange += 1;
        } else {
          hasDividers = true;
          dividerShelves += 1;
          if (shelfWidth > 0 && toFiniteNumber(active.width) >= shelfWidth) {
            widthNotBelowShelf += 1;
          } else {
            trustedHardware.push({
              width: toFiniteNumber(active.width),
              height: toFiniteNumber(active.height),
              depth: toFiniteNumber(active.depth),
              atStart: Boolean(active.atStart),
              atEnd: Boolean(active.atEnd),
            });
          }
        }
      }

      for (const item of items) {
        const facings = Math.max(1, toFiniteNumber(item.facings));
        const entry = facingsByProduct.get(item.productCode) ?? { onDividerShelves: 0, total: 0 };
        entry.total += facings;
        if (hasDividers) entry.onDividerShelves += facings;
        facingsByProduct.set(item.productCode, entry);
      }
    }
  }

  let productsNeedingDividers = 0;
  for (const entry of facingsByProduct.values()) {
    if (entry.onDividerShelves * 2 > entry.total) productsNeedingDividers += 1;
  }

  return {
    shelves,
    dividerShelves,
    products: facingsByProduct.size,
    productsNeedingDividers,
    facingsByProduct,
    hardware: mostCommon(trustedHardware, hardwareKey),
    widthNotBelowShelf,
    flagsOutOfRange,
    typicalShelfWidth: mostCommon(shelfWidths, (width) => String(width)),
  } satisfies BaseDividerEvidence;
};

/** "form" -> "Form", "pack_size" -> "Pack Size". */
export const dividerAttributeLabel = (attribute: string) =>
  attribute.replace(/_/g, " ").replace(/\b[a-z]/g, (character) => character.toUpperCase());

export const DIVIDER_CDT_LEVEL_LABELS: Record<DividerCdtLevel, string> = {
  cdt0: "CDT0",
  cdt1: "CDT1",
  cdt2: "CDT2",
  cdt3: "CDT3",
};

const normalizeValue = (value: DividerAttributeValue) =>
  value === null || value === undefined ? "" : String(value).trim().toLowerCase();

const displayValue = (value: DividerAttributeValue) =>
  value === null || value === undefined ? "" : String(value).trim();

export interface ProductDividerNeed extends ItemDividerNeed {
  /** Base source only: the product is not in the base planogram. */
  isNewToBase?: boolean;
}

export interface DividerPolicyResolution {
  source: DividerPolicySource;
  needs: Map<string, ProductDividerNeed>;
  /** Null when neither the policy nor the base planogram gives a usable size. */
  hardware: DividerHardware | null;
  /** Some product needs dividers and there is a size to give them. */
  followsProducts: boolean;
  /** Store shelves whose fixture record has valid dividers keep them (see `DividerPolicy`). */
  keepFixtureDividers: boolean;
  /** The same rule's answer for spring loaders (DEV-3518). */
  loaders: LoaderPolicyResolution;
}

/** The policy's own size when it has a width, else the base planogram's. */
export const resolveDividerHardware = (
  policy: DividerPolicy | null | undefined,
  evidence: Pick<BaseDividerEvidence, "hardware"> | null,
): DividerHardware | null => {
  const own = policy?.hardware;
  if (own && toFiniteNumber(own.width) > 0) {
    return {
      width: Math.round(toFiniteNumber(own.width)),
      height: Math.max(0, Math.round(toFiniteNumber(own.height))),
      depth: Math.max(0, Math.round(toFiniteNumber(own.depth))),
      atStart: Boolean(own.atStart),
      atEnd: Boolean(own.atEnd),
    };
  }
  return evidence?.hardware ?? null;
};

/**
 * Which products need dividers under the flow's policy, each with a reason.
 *
 * - base: a product needs them when more than half of its base facings sit on
 *   shelves with active dividers. A product missing from the base does not
 *   (`isNewToBase`).
 * - attribute / cdt (legacy rules): the product's value is one of the chosen
 *   values (trimmed, case-insensitive); `reasonValue` holds the product's value.
 * - items: the product's code is one of the ticked codes.
 * - none, or no policy: nothing needs dividers.
 * Base and items have no reason: the shelf's facing counts already explain them.
 */
export const resolveDividerPolicy = ({
  policy,
  products,
  basePog,
  evidence: givenEvidence,
  baseLibraryShelves,
  loaderEvidence: givenLoaderEvidence,
}: {
  policy: DividerPolicy | null | undefined;
  products: readonly DividerPolicyProduct[];
  basePog?: DividerBasePog | null;
  evidence?: BaseDividerEvidence;
  baseLibraryShelves?: readonly LoaderLibraryShelf[];
  loaderEvidence?: BaseLoaderEvidence;
}): DividerPolicyResolution => {
  const evidence = givenEvidence ?? getBaseDividerEvidence(basePog);
  const needs = new Map<string, ProductDividerNeed>();
  const source = policy?.source ?? "none";
  const hardware = resolveDividerHardware(policy, evidence);
  const keepFixtureDividers = policy?.keepFixtureDividers === true;
  const loaders = resolveLoaderPolicy({
    policy,
    products,
    evidence: givenLoaderEvidence ?? getBaseLoaderEvidence(basePog, baseLibraryShelves),
  });

  if (!policy || source === "none") {
    return {
      source: "none",
      needs,
      hardware,
      followsProducts: false,
      keepFixtureDividers,
      loaders,
    };
  }

  for (const product of products) {
    needs.set(product.productCode, needOf(policy, product, evidence));
  }

  const anyNeeded = [...needs.values()].some((need) => need.needed);
  return {
    source,
    needs,
    hardware,
    followsProducts: anyNeeded && hardware !== null && hardware.width > 0,
    keepFixtureDividers,
    loaders,
  };
};

const needOf = (
  policy: DividerPolicy,
  product: DividerPolicyProduct,
  evidence: BaseDividerEvidence,
): ProductDividerNeed => {
  if (policy.source === "base") {
    const facings = evidence.facingsByProduct.get(product.productCode);
    if (!facings) return { needed: false, reason: "", isNewToBase: true };
    return { needed: facings.onDividerShelves * 2 > facings.total, reason: "" };
  }

  if (policy.source === "items") {
    return { needed: new Set(policy.productCodes ?? []).has(product.productCode), reason: "" };
  }

  const [kind, field, raw, chosen] =
    policy.source === "attribute"
      ? ([
          "attribute",
          policy.attribute ?? "",
          product.attributes?.[policy.attribute ?? ""],
          policy.values ?? [],
        ] as const)
      : ([
          "cdt",
          policy.cdtLevel ?? "cdt0",
          product.cdt?.[policy.cdtLevel ?? "cdt0"],
          policy.cdtValues ?? [],
        ] as const);
  const value = displayValue(raw);
  const reasonValue = { kind, field, value };
  if (!value) return { needed: false, reason: "", reasonValue };
  const wanted = new Set(chosen.map(normalizeValue));
  return { needed: wanted.has(normalizeValue(raw)), reason: "", reasonValue };
};

/** Values of one attribute (or CDT level) with product counts, most common first. */
export const countDividerValues = (
  products: readonly DividerPolicyProduct[],
  valueOf: (product: DividerPolicyProduct) => DividerAttributeValue,
): { value: string; count: number }[] => {
  const counts = new Map<string, { value: string; count: number }>();
  for (const product of products) {
    const value = displayValue(valueOf(product));
    if (!value) continue;
    const key = normalizeValue(value);
    const entry = counts.get(key) ?? { value, count: 0 };
    entry.count += 1;
    counts.set(key, entry);
  }
  return [...counts.values()].sort((a, b) => b.count - a.count || a.value.localeCompare(b.value));
};

export interface DividerAttributeSuggestion {
  attribute: string;
  values: string[];
  /** Base products needing dividers that the rule covers, of `needing`. */
  covered: number;
  needing: number;
  /** Base products the rule would add that do not sit behind dividers today. */
  added: number;
}

/** Below this many base products behind dividers nothing is suggested. */
export const DIVIDER_SUGGESTION_MIN_PRODUCTS = 3;
/** A rule must cover at least this share of the products behind dividers, and add at most 1 - it. */
export const DIVIDER_SUGGESTION_MIN_SHARE = 0.8;
/** Attributes with more distinct values than this among base products are names or ids, not types. */
const MAX_SUGGESTION_VALUES = 12;

/**
 * An attribute rule that reproduces the base planogram's divider products:
 * the values most of whose base products sit behind dividers. Null when no
 * attribute covers at least 80% of them while adding at most 20% others.
 */
export const suggestDividerAttributeRule = (
  evidence: BaseDividerEvidence,
  products: readonly DividerPolicyProduct[],
): DividerAttributeSuggestion | null => {
  const baseProducts = products.filter((product) =>
    evidence.facingsByProduct.has(product.productCode),
  );
  const isNeeding = (product: DividerPolicyProduct) => {
    const facings = evidence.facingsByProduct.get(product.productCode);
    return facings !== undefined && facings.onDividerShelves * 2 > facings.total;
  };
  const needing = baseProducts.filter(isNeeding).length;
  if (needing < DIVIDER_SUGGESTION_MIN_PRODUCTS) return null;

  const attributes = new Set<string>();
  for (const product of baseProducts) {
    for (const key of Object.keys(product.attributes ?? {})) attributes.add(key);
  }

  let best: (DividerAttributeSuggestion & { agreement: number }) | null = null;
  for (const attribute of [...attributes].sort()) {
    const byValue = new Map<string, { value: string; needing: number; total: number }>();
    for (const product of baseProducts) {
      const value = displayValue(product.attributes?.[attribute]);
      if (!value) continue;
      const entry = byValue.get(normalizeValue(value)) ?? { value, needing: 0, total: 0 };
      entry.total += 1;
      if (isNeeding(product)) entry.needing += 1;
      byValue.set(normalizeValue(value), entry);
    }
    if (byValue.size < 2 || byValue.size > MAX_SUGGESTION_VALUES) continue;

    const chosen = [...byValue.values()].filter((entry) => entry.needing * 2 > entry.total);
    if (chosen.length === 0 || chosen.length === byValue.size) continue;
    const covered = chosen.reduce((total, entry) => total + entry.needing, 0);
    const inRule = chosen.reduce((total, entry) => total + entry.total, 0);
    const added = inRule - covered;
    if (covered < DIVIDER_SUGGESTION_MIN_SHARE * needing) continue;
    if (added > (1 - DIVIDER_SUGGESTION_MIN_SHARE) * inRule) continue;

    const agreement = covered - added;
    const values = chosen
      .sort((a, b) => b.needing - a.needing || a.value.localeCompare(b.value))
      .map((entry) => entry.value);
    if (
      !best ||
      agreement > best.agreement ||
      (agreement === best.agreement && values.length < best.values.length)
    ) {
      best = { attribute, values, covered, needing, added, agreement };
    }
  }
  if (!best) return null;
  const { agreement: _agreement, ...suggestion } = best;
  return suggestion;
};

/**
 * Facings of one width that fit on a shelf with and without dividers between
 * every facing, by the pogger's arithmetic: n facings take
 * n * width + (n - 1 + start + end) * divider.
 */
export const estimateFacingsWithDividers = ({
  shelfWidth,
  facingWidth,
  hardware,
}: {
  shelfWidth: number;
  facingWidth: number;
  hardware: DividerHardware;
}) => {
  if (!(facingWidth > 0) || !(shelfWidth > 0)) return { without: 0, with: 0 };
  const ends = Number(hardware.atStart) + Number(hardware.atEnd);
  const divider = Math.max(0, hardware.width);
  return {
    without: Math.floor(shelfWidth / facingWidth),
    with: Math.max(0, Math.floor((shelfWidth - (ends - 1) * divider) / (facingWidth + divider))),
  };
};

export interface ShelfDividerNeedSummary {
  /** Facing columns of products that need dividers. */
  needing: number;
  columns: number;
  /** Strictly more than half: the pogger's follow-products rule. */
  isMajority: boolean;
  /** Most facings first; only a legacy attribute / CDT rule has any to show. */
  reasons: ShelfDividerReason[];
}

/** A legacy rule's reason: the product's value to word, or the text an older pog stored. */
export type ShelfDividerReason =
  | ({ code: "value" } & ItemDividerReasonValue)
  | { code: "stored"; text: string };

/** Stored by earlier pogs for the base and ticked sources; they name our data model, not the shelf. */
const UNSHOWN_DIVIDER_REASONS = new Set([
  "",
  "Picked by hand",
  "Not picked",
  "On a divider shelf in the base planogram",
  "Not on a divider shelf in the base planogram",
  "New: not in the base planogram, so no dividers",
]);

const reasonOf = (need: ItemDividerNeed): ShelfDividerReason | null => {
  if (need.reasonValue) return { code: "value", ...need.reasonValue };
  const text = need.reason ?? "";
  return UNSHOWN_DIVIDER_REASONS.has(text) ? null : { code: "stored", text };
};

const reasonKey = (reason: ShelfDividerReason) =>
  reason.code === "value" ? `${reason.kind}:${reason.field}:${reason.value}` : reason.text;

/** How many of a shelf's facing columns belong to products that need dividers. */
export const summarizeShelfDividerNeeds = (
  items: readonly { columns: number; dividerNeed?: ItemDividerNeed | null }[],
): ShelfDividerNeedSummary | null => {
  if (!items.some((item) => item.dividerNeed)) return null;
  let needing = 0;
  let columns = 0;
  const reasonColumns = new Map<string, { reason: ShelfDividerReason; columns: number }>();
  for (const item of items) {
    const itemColumns = Math.max(0, toFiniteNumber(item.columns));
    columns += itemColumns;
    if (item.dividerNeed?.needed) {
      needing += itemColumns;
      const reason = reasonOf(item.dividerNeed);
      if (reason) {
        const key = reasonKey(reason);
        const entry = reasonColumns.get(key) ?? { reason, columns: 0 };
        entry.columns += itemColumns;
        reasonColumns.set(key, entry);
      }
    }
  }
  const reasons = [...reasonColumns.entries()]
    .sort((a, b) => b[1].columns - a[1].columns || a[0].localeCompare(b[0]))
    .map(([, { reason }]) => reason);
  return { needing, columns, isMajority: needing * 2 > columns, reasons };
};

/**
 * Why a shelf has dividers or not, as a code and values the frontend words. `reasons` are a
 * legacy attribute / CDT rule's item reasons, at most two.
 */
export type ShelfDividerWhy =
  | { code: "set-always" }
  | { code: "kept-from-fixture" }
  | { code: "set-never"; needing?: number; facings?: number }
  | { code: "not-needed" }
  | {
      code: "needed";
      needing: number;
      facings: number;
      reasons: ShelfDividerReason[];
      needsMoreThanHalf: boolean;
    };

/** Null when no policy judged the shelf. */
export const shelfDividerWhy = ({
  summary,
  dividers,
  setByHand,
  keptFromFixture,
}: {
  summary: ShelfDividerNeedSummary | null;
  dividers: PlanogramShelfDividers | null | undefined;
  setByHand: DividersSetByHand | null | undefined;
  /** The shelf's `dividersKeptFromFixture`: the keep switch kept the fixture row's dividers. */
  keptFromFixture?: boolean | null;
}): ShelfDividerWhy | null => {
  if (setByHand === "always") return { code: "set-always" };
  if (keptFromFixture && !setByHand) return { code: "kept-from-fixture" };
  if (setByHand === "never") {
    return summary
      ? { code: "set-never", needing: summary.needing, facings: summary.columns }
      : { code: "set-never" };
  }
  if (!summary) return null;
  if (summary.needing === 0) return { code: "not-needed" };
  return {
    code: "needed",
    needing: summary.needing,
    facings: summary.columns,
    reasons: summary.reasons.slice(0, 2),
    needsMoreThanHalf: !getActiveShelfDividers(dividers),
  };
};

export type ShelfDividerNeedProblem = "needed-without-dividers" | "dividers-without-need";

/**
 * Where a solved shelf disagrees with the products on it. Only shelves whose
 * items carry a policy result are judged. A product that needs dividers on a
 * shelf without them is flagged even when the majority rule chose that, so the
 * reviewer sees it. A hand-set "always" shelf, and a shelf whose dividers were
 * kept from its store fixture (the keep switch, sent like "always"), are never
 * flagged as unneeded.
 */
export const getShelfDividerNeedProblems = (shelf: {
  dividers?: PlanogramShelfDividers | null;
  dividersSetByHand?: DividersSetByHand | null;
  dividersKeptFromFixture?: boolean | null;
  items: readonly { dividerNeed?: ItemDividerNeed | null }[];
}): ShelfDividerNeedProblem[] => {
  if (!shelf.items.some((item) => item.dividerNeed)) return [];
  const anyNeeded = shelf.items.some((item) => item.dividerNeed?.needed);
  const hasDividers = getActiveShelfDividers(shelf.dividers) !== null;
  if (anyNeeded && !hasDividers) return ["needed-without-dividers"];
  const exempt = shelf.dividersSetByHand === "always" || shelf.dividersKeptFromFixture === true;
  if (!anyNeeded && hasDividers && !exempt) {
    return ["dividers-without-need"];
  }
  return [];
};

/** A base store Fixtures page row; `shelfNo` counts from the top, like the requests' `shelf_no`. */
export interface LoaderLibraryShelf {
  bayNo: number;
  shelfNo: number;
  /** null = not set (follow the rule), 0 = never a loader, > 0 = always this deep (x100). */
  loaderDepth: number | string | null | undefined;
}

/**
 * "psa": the base pog shelf's own value (its PSA back overhang), also when the
 * base store's row only repeats it; "library": a different value on the base
 * store's Fixtures page row.
 */
export type LoaderDepthSource = "psa" | "library";

export interface LoaderBaseShelf {
  bayNo: number;
  shelfNo: number;
  /** A regular shelf holding products: one of the O line's "{t} shelves". */
  counted: boolean;
  physicalDepth: number;
  items: { productCode: string; facings: number }[];
  /** The Fixtures page row's depth when set there, else the shelf's own; null for none. */
  loaderDepth: number | null;
  depthSource: LoaderDepthSource | null;
  /** undefined = the base store's row does not set one. */
  libraryDepth?: number;
}

export interface BaseLoaderEvidence {
  shelves: number;
  products: number;
  /** More than half of their facings on loader shelves: the default ticks. */
  productsNeedingLoaders: number;
  facingsByProduct: Map<string, { onLoaderShelves: number; total: number }>;
  loaderShelves: number;
  /** Distinct, ascending, x100. */
  depths: number[];
  /** The sizes row's prefill. */
  typicalDepth: number | null;
  depthSource: LoaderDepthSource | "mixed" | null;
  /** The flow's default depth must stay below it. */
  shallowestShelfDepth: number | null;
  shelvesByPosition: Map<string, LoaderBaseShelf>;
}

export const loaderShelfKey = (bayNo: number, shelfNo: number) => `${bayNo}:${shelfNo}`;

/** undefined = not set, so 0 ("never") stays distinguishable from blank. */
const libraryLoaderValue = (value: unknown): number | undefined => {
  if (value === null || value === undefined || value === "") return undefined;
  if (!Number.isFinite(Number(value))) return undefined;
  return normalizeLoaderDepth(value);
};

/** Numbered top first like the request builders; stored order breaks notch ties. */
const shelvesTopFirst = <T extends { notchNo?: number }>(shelves: readonly T[]) =>
  shelves
    .map((shelf, index) => ({ shelf, index }))
    .sort(
      (a, b) =>
        toFiniteNumber(b.shelf.notchNo) - toFiniteNumber(a.shelf.notchNo) || a.index - b.index,
    )
    .map(({ shelf }, index) => ({ shelf, shelfNo: index + 1 }));

/** The base store's Fixtures page row wins over the base pog shelf's own (PSA) loader, 0 included. */
export const getBaseLoaderEvidence = (
  basePog: DividerBasePog | null | undefined,
  libraryShelves: readonly LoaderLibraryShelf[] = [],
): BaseLoaderEvidence => {
  const library = new Map<string, number>();
  for (const row of libraryShelves) {
    const value = libraryLoaderValue(row.loaderDepth);
    if (value !== undefined) library.set(loaderShelfKey(row.bayNo, row.shelfNo), value);
  }

  const shelvesByPosition = new Map<string, LoaderBaseShelf>();
  const facingsByProduct = new Map<string, { onLoaderShelves: number; total: number }>();
  const loaderDepths: number[] = [];
  const sources = new Set<LoaderDepthSource>();
  let shelves = 0;
  let loaderShelves = 0;
  let shallowestShelfDepth: number | null = null;

  (basePog?.planogram?.bays ?? []).forEach((bay, bayIndex) => {
    const bayNo = bayIndex + 1;
    for (const { shelf, shelfNo } of shelvesTopFirst(bay.shelves ?? [])) {
      const key = loaderShelfKey(bayNo, shelfNo);
      const items = (shelf.items ?? []).map((item) => ({
        productCode: item.productCode,
        facings: Math.max(1, toFiniteNumber(item.facings)),
      }));
      const libraryValue = library.get(key);
      const ownDepth = normalizeLoaderDepth(shelf.loaderDepth);
      const loaderDepth =
        libraryValue !== undefined
          ? libraryValue > 0
            ? libraryValue
            : null
          : ownDepth > 0
            ? ownDepth
            : null;
      // A PSA fixture upload writes the base store's row from the same negative
      // back overhang the base pog shelf was imported with; a row that only
      // repeats the shelf's own depth still comes from the base planogram.
      const depthSource: LoaderDepthSource | null =
        loaderDepth === null
          ? null
          : libraryValue !== undefined && libraryValue !== ownDepth
            ? "library"
            : "psa";
      const counted = (!shelf.type || shelf.type === "REGULAR") && items.length > 0;
      const physicalDepth = toFiniteNumber(shelf.depth);
      shelvesByPosition.set(key, {
        bayNo,
        shelfNo,
        counted,
        physicalDepth,
        items,
        loaderDepth,
        depthSource,
        ...(libraryValue !== undefined ? { libraryDepth: libraryValue } : {}),
      });
      if (!counted) continue;

      shelves += 1;
      if (physicalDepth > 0) {
        shallowestShelfDepth =
          shallowestShelfDepth === null
            ? physicalDepth
            : Math.min(shallowestShelfDepth, physicalDepth);
      }
      if (loaderDepth !== null && depthSource) {
        loaderShelves += 1;
        loaderDepths.push(loaderDepth);
        sources.add(depthSource);
      }
      for (const item of items) {
        const entry = facingsByProduct.get(item.productCode) ?? { onLoaderShelves: 0, total: 0 };
        entry.total += item.facings;
        if (loaderDepth !== null) entry.onLoaderShelves += item.facings;
        facingsByProduct.set(item.productCode, entry);
      }
    }
  });

  let productsNeedingLoaders = 0;
  for (const entry of facingsByProduct.values()) {
    if (entry.onLoaderShelves * 2 > entry.total) productsNeedingLoaders += 1;
  }

  return {
    shelves,
    products: facingsByProduct.size,
    productsNeedingLoaders,
    facingsByProduct,
    loaderShelves,
    depths: [...new Set(loaderDepths)].sort((a, b) => a - b),
    typicalDepth: mostCommon(loaderDepths, (depth) => String(depth)),
    depthSource: sources.size === 0 ? null : sources.size > 1 ? "mixed" : [...sources][0],
    shallowestShelfDepth,
    shelvesByPosition,
  };
};

export interface LoaderPolicyResolution {
  /** A stored rule whose source is not "none"; inactive means no product is judged. */
  active: boolean;
  needs: Map<string, ItemLoaderNeed>;
  anyNeeded: boolean;
  /** null: each shelf falls back to the base shelf's own depth, else `baseTypicalDepth`. */
  defaultDepth: number | null;
  /**
   * The evidence's `typicalDepth`: the base planogram's most common loader depth
   * (the base store's Fixtures page rows win over the PSA's, as everywhere).
   * Mode "products" offers it on a shelf whose base shelf has no loader when the
   * flow sets no depth; null when the base has no loader anywhere.
   */
  baseTypicalDepth: number | null;
  /** The rule's `loaderMode`; "products" when absent or without a rule. */
  mode: LoaderMode;
  /** For builders that see only fixture rows (UHD) and for the response. */
  baseShelves: ReadonlyMap<string, LoaderBaseShelf>;
}

/** Absent (every rule written before the switch) follows the products. */
export const loaderModeOf = (policy: Pick<DividerPolicy, "loaderMode"> | null | undefined) =>
  policy?.loaderMode === "shelf" ? "shelf" : "products";

/** Source "base" reads the base's loader shelves; every other source reads `loaderProductCodes`. */
export const resolveLoaderPolicy = ({
  policy,
  products,
  evidence,
}: {
  policy: DividerPolicy | null | undefined;
  products: readonly DividerPolicyProduct[];
  evidence: BaseLoaderEvidence;
}): LoaderPolicyResolution => {
  const needs = new Map<string, ItemLoaderNeed>();
  const defaultDepth = normalizeLoaderDepth(policy?.loaderDepth) || null;
  const baseShelves = evidence.shelvesByPosition;
  const mode = loaderModeOf(policy);
  const baseTypicalDepth = evidence.typicalDepth;
  if (!policy || policy.source === "none") {
    return {
      active: false,
      needs,
      anyNeeded: false,
      defaultDepth,
      baseTypicalDepth,
      mode,
      baseShelves,
    };
  }

  const ticked = new Set(policy.loaderProductCodes ?? []);
  for (const { productCode } of products) {
    if (policy.source === "base") {
      const facings = evidence.facingsByProduct.get(productCode);
      needs.set(
        productCode,
        !facings
          ? { needed: false, reason: "new-to-base" }
          : facings.onLoaderShelves * 2 > facings.total
            ? { needed: true, reason: "on-loader-shelf-in-base" }
            : { needed: false, reason: "not-on-loader-shelf-in-base" },
      );
    } else {
      needs.set(
        productCode,
        ticked.has(productCode)
          ? { needed: true, reason: "ticked" }
          : { needed: false, reason: "not-ticked" },
      );
    }
  }
  const anyNeeded = [...needs.values()].some((need) => need.needed);
  return { active: true, needs, anyNeeded, defaultDepth, baseTypicalDepth, mode, baseShelves };
};

/**
 * `mode` absent reads "products"; `anyNeeded` absent is derived from `needs`;
 * `baseTypicalDepth` absent is none.
 */
export type ShelfLoaderRule = Pick<LoaderPolicyResolution, "active" | "needs" | "defaultDepth"> &
  Partial<Pick<LoaderPolicyResolution, "mode" | "anyNeeded" | "baseTypicalDepth">>;

const ruleNeedsAnyLoader = (rule: ShelfLoaderRule) =>
  rule.anyNeeded ?? [...rule.needs.values()].some((need) => need.needed);

/**
 * Mode "products": the flow's depth, else the base shelf's own, else the base
 * planogram's typical loader depth ("base-typical"). Null when none is known.
 */
const followProductsDepth = (
  rule: ShelfLoaderRule,
  baseShelfDepth: unknown,
): { depth: number; depthSource?: ShelfLoaderDepthSource } | null => {
  if (rule.defaultDepth) return { depth: rule.defaultDepth };
  const own = normalizeLoaderDepth(baseShelfDepth);
  if (own > 0) return { depth: own };
  const typical = normalizeLoaderDepth(rule.baseTypicalDepth);
  return typical > 0 ? { depth: typical, depthSource: "base-typical" } : null;
};

/** The pogger shelf `loader` block, in the request's x100 units. */
export interface ShelfLoaderOffer {
  depth: number;
  mode: ShelfLoaderBlockMode;
}

/**
 * The `loader` block the request sends for one store shelf, or null for none.
 *
 * - A Fixtures page row > 0: `fixed` at that depth (the hand "always"); 0: none (never).
 * - Mode "products", under a rule some product's need is ticked or read from the base:
 *   `follow_products` at the flow's depth, else the base shelf's own, else (a base shelf
 *   without a loader) the base planogram's typical loader depth. The pogger puts it in
 *   iff more than half of the shelf's facing columns need a loader.
 * - Mode "shelf": `fixed` at the base shelf's own depth when it has a loader; else none.
 * - No depth known, or no rule: none.
 */
export const shelfLoaderOffer = ({
  libraryDepth,
  baseShelf,
  rule,
}: {
  libraryDepth?: unknown;
  baseShelf?: Pick<LoaderBaseShelf, "loaderDepth"> | null;
  rule?: ShelfLoaderRule | null;
}): ShelfLoaderOffer | null => {
  const library = libraryLoaderValue(libraryDepth);
  if (library !== undefined) return library > 0 ? { depth: library, mode: "fixed" } : null;
  if (!rule) return null;
  const baseDepth = normalizeLoaderDepth(baseShelf?.loaderDepth);
  if (rule.mode === "shelf") return baseDepth > 0 ? { depth: baseDepth, mode: "fixed" } : null;
  if (!rule.active || !ruleNeedsAnyLoader(rule)) return null;
  const depth = followProductsDepth(rule, baseDepth);
  return depth ? { depth: depth.depth, mode: "follow_products" } : null;
};

/**
 * The Fixtures page row wins; else, mode "shelf", the base shelf's own loader; else more than
 * half of the base facings decide, with the flow's depth, else the base shelf's, else the base
 * planogram's typical loader depth (`depthSource: "base-typical"`, set whenever that is the
 * depth the request offers, whatever the reason). Runs before
 * the solve: for mode "products" it is the O line's prediction, and the solved shelf replaces
 * it with the pogger's decision (`solvedShelfLoaderDecision`).
 */
export const decideShelfLoader = ({
  libraryDepth,
  baseShelf,
  rule,
}: {
  /** `master_fixture.loader_depth` of the store's row. */
  libraryDepth?: unknown;
  baseShelf?: Pick<LoaderBaseShelf, "items" | "loaderDepth"> | null;
  rule?: ShelfLoaderRule | null;
}): ShelfLoaderDecision => {
  let needing = 0;
  let facings = 0;
  for (const item of baseShelf?.items ?? []) {
    const itemFacings = Math.max(1, toFiniteNumber(item.facings));
    facings += itemFacings;
    if (rule?.active && rule.needs.get(item.productCode)?.needed) needing += itemFacings;
  }

  const library = libraryLoaderValue(libraryDepth);
  if (library !== undefined) {
    return library > 0
      ? { reason: "set-on-fixtures-page", depth: library, needing, facings }
      : { reason: "set-none-on-fixtures-page", depth: null, needing, facings };
  }
  if (rule?.mode === "shelf") {
    const depth = normalizeLoaderDepth(baseShelf?.loaderDepth);
    return depth > 0
      ? { reason: "on-base-shelf", depth, needing, facings }
      : { reason: "not-on-base-shelf", depth: null, needing, facings };
  }
  if (!rule?.active) return { reason: "not-needed", depth: null, needing, facings };
  // The depth `shelfLoaderOffer` sends. The pogger decides on the solved shelf's own
  // products, so the loader can go in whatever the base shelf's majority said: its
  // source is kept on every reason, and the solved decision drops it when it stays out.
  const offered = ruleNeedsAnyLoader(rule)
    ? followProductsDepth(rule, baseShelf?.loaderDepth)
    : null;
  const source = offered?.depthSource ? { depthSource: offered.depthSource } : {};
  if (needing === 0) return { reason: "not-needed", depth: null, needing, facings, ...source };
  if (needing * 2 <= facings) {
    return { reason: "needs-more-than-half", depth: null, needing, facings, ...source };
  }
  if (!offered) return { reason: "depth-not-set", depth: null, needing, facings };
  return { reason: "needed", depth: offered.depth, needing, facings, ...source };
};

/** The pogger's `PogShelf.loader` (`PogLoader`), as far as the pog keeps it. */
export interface SolvedShelfLoader {
  depth: number;
  mode: string;
  active?: boolean | null;
  needs_loader_facings?: number | null;
}

/**
 * Folds the pogger's decision into the decision the request was built from. A
 * "follow_products" shelf then states the solved outcome: "needed" at the
 * loader's depth when it went in, else "needs-more-than-half" (some facings
 * needed one, not most) or "not-needed", with no depth (and no `depthSource`).
 * A fixed shelf keeps its reason ("set-on-fixtures-page", "on-base-shelf"). No
 * `loader` (the request sent none): unchanged.
 */
export const solvedShelfLoaderDecision = (
  decision: ShelfLoaderDecision,
  loader: SolvedShelfLoader | null | undefined,
): ShelfLoaderDecision => {
  if (!loader) return decision;
  const mode: ShelfLoaderBlockMode =
    loader.mode === "follow_products" ? "follow_products" : "fixed";
  // The pogger reports a fixed loader as always in.
  const active = typeof loader.active === "boolean" ? loader.active : mode === "fixed";
  const needFacings = Math.max(0, Math.round(toFiniteNumber(loader.needs_loader_facings)));
  const solved: ShelfLoaderDecision = { ...decision, mode, active, needFacings };
  if (mode === "fixed") return solved;
  if (active) {
    return {
      ...solved,
      reason: "needed",
      depth: normalizeLoaderDepth(loader.depth) || decision.depth,
    };
  }
  const out: ShelfLoaderDecision = {
    ...solved,
    reason: needFacings > 0 ? "needs-more-than-half" : "not-needed",
    depth: null,
  };
  delete out.depthSource;
  return out;
};

/** The loader O line's {n} of {m} products, {s} of {t} shelves, {d} depths and {k} "depth not set". */
export interface BaseLoaderSummary {
  productsNeeding: number;
  products: number;
  shelves: number;
  loaderShelves: number;
  depths: number[];
  depthNotSet: number;
  /** The result panel, in bay / shelf order. */
  rows: { bayNo: number; shelfNo: number; decision: ShelfLoaderDecision }[];
}

/** The base store's Fixtures page rows act as hand settings, as when that store is generated. */
export const summarizeBaseLoaders = ({
  evidence,
  loaders,
}: {
  evidence: BaseLoaderEvidence;
  loaders: LoaderPolicyResolution;
}): BaseLoaderSummary => {
  const rows: BaseLoaderSummary["rows"] = [];
  const depths = new Set<number>();
  let loaderShelves = 0;
  let depthNotSet = 0;
  for (const shelf of evidence.shelvesByPosition.values()) {
    if (!shelf.counted) continue;
    const decision = decideShelfLoader({
      libraryDepth: shelf.libraryDepth,
      baseShelf: shelf,
      rule: loaders,
    });
    rows.push({ bayNo: shelf.bayNo, shelfNo: shelf.shelfNo, decision });
    if (decision.depth !== null) {
      loaderShelves += 1;
      depths.add(decision.depth);
    }
    if (decision.reason === "depth-not-set") depthNotSet += 1;
  }
  rows.sort((a, b) => a.bayNo - b.bayNo || a.shelfNo - b.shelfNo);
  return {
    productsNeeding: [...loaders.needs.values()].filter((need) => need.needed).length,
    products: evidence.products,
    shelves: evidence.shelves,
    loaderShelves,
    depths: [...depths].sort((a, b) => a - b),
    depthNotSet,
    rows,
  };
};

/** `loaders` on GET /merchflow/base-pog/:basePogId. Lengths are x100. */
export interface BaseLoaderEvidencePayload {
  shelves: number;
  products: number;
  productsNeedingLoaders: number;
  /** Base shelves that carry a loader themselves. */
  baseLoaderShelves: number;
  depths: number[];
  typicalDepth: number | null;
  depthSource: LoaderDepthSource | "mixed" | null;
  shallowestShelfDepth: number | null;
  /** The untouched rule (`{ source: "base" }`) applied to the base: the O line's {s}, {d}, {k}. */
  loaderShelves: number;
  loaderDepths: number[];
  loaderShelvesWithoutDepth: number;
  /** Lets the modal recompute {s} after the ticks change. */
  libraryShelves: { bayNo: number; shelfNo: number; loaderDepth: number | null }[];
  /**
   * The O line's numbers under each loader mode. `products` repeats the three
   * fields above; `shelf` gives "Spring loaders stay on the base planogram's
   * {s} shelves, {d} {unit} deep." (no "depth not set": a shelf mode loader
   * always has its hardware's depth).
   */
  byMode: Record<LoaderMode, BaseLoaderModeNumbers>;
}

export interface BaseLoaderModeNumbers {
  /** {s} */
  loaderShelves: number;
  /** {d}, or {d1} to {d2} when more than one. Ascending, x100. */
  loaderDepths: number[];
  /** {k} */
  loaderShelvesWithoutDepth: number;
}

export const getBaseLoaderEvidencePayload = (
  basePog: DividerBasePog | null | undefined,
  libraryShelves: readonly LoaderLibraryShelf[] = [],
): BaseLoaderEvidencePayload => {
  const evidence = getBaseLoaderEvidence(basePog, libraryShelves);
  const products = [...evidence.facingsByProduct.keys()].map((productCode) => ({ productCode }));
  const summaryIn = (loaderMode: LoaderMode) =>
    summarizeBaseLoaders({
      evidence,
      loaders: resolveLoaderPolicy({ policy: { source: "base", loaderMode }, products, evidence }),
    });
  const summary = summaryIn("products");
  const shelfSummary = summaryIn("shelf");
  const numbers = (of: BaseLoaderSummary): BaseLoaderModeNumbers => ({
    loaderShelves: of.loaderShelves,
    loaderDepths: of.depths,
    loaderShelvesWithoutDepth: of.depthNotSet,
  });
  return {
    shelves: evidence.shelves,
    products: evidence.products,
    productsNeedingLoaders: evidence.productsNeedingLoaders,
    baseLoaderShelves: evidence.loaderShelves,
    depths: evidence.depths,
    typicalDepth: evidence.typicalDepth,
    depthSource: evidence.depthSource,
    shallowestShelfDepth: evidence.shallowestShelfDepth,
    loaderShelves: summary.loaderShelves,
    loaderDepths: summary.depths,
    loaderShelvesWithoutDepth: summary.depthNotSet,
    libraryShelves: libraryShelves
      .map((row) => ({
        bayNo: row.bayNo,
        shelfNo: row.shelfNo,
        loaderDepth: libraryLoaderValue(row.loaderDepth) ?? null,
      }))
      .sort((a, b) => a.bayNo - b.bayNo || a.shelfNo - b.shelfNo),
    byMode: { products: numbers(summary), shelf: numbers(shelfSummary) },
  };
};

/**
 * Codes and values, not copy: the frontend words them.
 * "needed-without-loader" and "loader-without-need" are the two violations'
 * shelves: the loader was decided per base shelf before the solve, and the
 * products moved.
 */
export type ShelfLoaderWhy =
  /** `typicalDepth`: the depth is the base planogram's typical one ("base-typical"). */
  | { code: "needed"; needing: number; facings: number; typicalDepth?: number }
  | { code: "needs-more-than-half"; needing: number; facings: number }
  | { code: "needed-without-loader"; needing: number; facings: number }
  | { code: "not-needed" }
  | { code: "loader-without-need" }
  | { code: "set-none-on-fixtures-page"; needing: number; facings: number }
  | { code: "set-on-fixtures-page"; depth: number }
  | { code: "depth-not-set"; needing: number; facings: number }
  | { code: "on-base-shelf"; depth: number; needing: number; facings: number }
  | { code: "not-on-base-shelf"; needing: number; facings: number };

/**
 * Counts come from the solved items when they carry `loaderNeed`, else from the base facings
 * the decision counted. Null for pogs generated before the rule.
 *
 * The stored decision is per base shelf, before the solve, so the shelf's own
 * products may disagree with it. "(needs more than half)" is said only when the
 * majority rule decided (`needs-more-than-half`) and the counts shown still fall
 * short of a majority; otherwise the line states what the shelf has.
 *
 * Judged items are counted in facing columns, the unit the pogger's majority
 * rule decides in. The pogger's `needFacings` counts every facing, stacked rows
 * included, so it is used only when no item carries `loaderNeed`.
 */
export const shelfLoaderWhy = ({
  loaderDepth,
  loaderDecision,
  items,
}: {
  loaderDepth: number | null | undefined;
  loaderDecision: ShelfLoaderDecision | null | undefined;
  items: readonly { columns: number; loaderNeed?: ItemLoaderNeed | null }[];
}): ShelfLoaderWhy | null => {
  const judged = items.some((item) => item.loaderNeed);
  if (!judged && !loaderDecision) return null;
  let needing = loaderDecision?.needing ?? 0;
  let facings = loaderDecision?.facings ?? 0;
  if (judged) {
    needing = 0;
    facings = 0;
    for (const item of items) {
      const columns = Math.max(0, toFiniteNumber(item.columns));
      facings += columns;
      if (item.loaderNeed?.needed) needing += columns;
    }
  }
  if (!judged && typeof loaderDecision?.needFacings === "number") {
    needing = loaderDecision.needFacings;
    facings = items.reduce((sum, item) => sum + Math.max(0, toFiniteNumber(item.columns)), 0);
  }
  const depth = normalizeLoaderDepth(loaderDepth);
  switch (loaderDecision?.reason) {
    case "set-on-fixtures-page":
      return { code: "set-on-fixtures-page", depth: depth || (loaderDecision.depth ?? 0) };
    case "set-none-on-fixtures-page":
      return { code: "set-none-on-fixtures-page", needing, facings };
    case "depth-not-set":
      return { code: "depth-not-set", needing, facings };
    case "on-base-shelf":
      return {
        code: "on-base-shelf",
        depth: depth || (loaderDecision.depth ?? 0),
        needing,
        facings,
      };
    case "not-on-base-shelf":
      return { code: "not-on-base-shelf", needing, facings };
    default:
      if (depth > 0) {
        if (needing === 0) return { code: "loader-without-need" };
        return loaderDecision?.depthSource === "base-typical"
          ? { code: "needed", needing, facings, typicalDepth: depth }
          : { code: "needed", needing, facings };
      }
      if (needing === 0) return { code: "not-needed" };
      return loaderDecision?.reason === "needs-more-than-half" && needing * 2 <= facings
        ? { code: "needs-more-than-half", needing, facings }
        : { code: "needed-without-loader", needing, facings };
  }
};

export type ShelfLoaderNeedProblem = "needed-without-loader" | "loader-without-need";

/**
 * Flagged even when the majority rule left the shelf bare; a Fixtures page depth is the hand
 * "always", so it is never flagged as unneeded. Pogs without `loaderNeed` are not judged.
 * A shelf the pogger decided reads its `loaderDecision.active`; others read `loaderDepth`.
 */
export const getShelfLoaderNeedProblems = (shelf: {
  loaderDepth?: number | null;
  loaderDecision?: ShelfLoaderDecision | null;
  items: readonly { loaderNeed?: ItemLoaderNeed | null }[];
}): ShelfLoaderNeedProblem[] => {
  if (!shelf.items.some((item) => item.loaderNeed)) return [];
  const anyNeeded = shelf.items.some((item) => item.loaderNeed?.needed);
  const active = shelf.loaderDecision?.active;
  const hasLoader =
    typeof active === "boolean" ? active : normalizeLoaderDepth(shelf.loaderDepth) > 0;
  if (anyNeeded && !hasLoader) return ["needed-without-loader"];
  if (!anyNeeded && hasLoader && shelf.loaderDecision?.reason !== "set-on-fixtures-page") {
    return ["loader-without-need"];
  }
  return [];
};

/**
 * One row of the base store's fixtures as the rules modal reads it (GET
 * /merchflow/base-pog/:basePogId `fixtureDividerRows`). The backend builds it
 * from the `master_fixture` row with the same guard its keep switch uses.
 */
export interface FixtureDividerRow {
  /** `section_num` / `shelf_num`: matched to the base pog's bay and shelf in stored order, like the rebuild request. */
  bayNo: number;
  shelfNo: number;
  /** The row's divider columns as a pog shelf states them (x100); null when the row states none. */
  dividers: PlanogramShelfDividers | null;
  dividersSetByHand: DividersSetByHand | null;
  /**
   * The DEV-3516 guard trusts the row's dividers (no x100 switches, narrower
   * than the shelf), they are between facings and wider than 0.
   */
  valid: boolean;
}

/**
 * The keep switch applies to this fixture row: the rule keeps fixture
 * dividers, the row has no hand override and its dividers are valid. The
 * products on the shelf do not matter; the request sends the row like a hand
 * "always" either way.
 */
export const fixtureRowKeepsDividers = (
  policy: Pick<DividerPolicy, "keepFixtureDividers"> | null | undefined,
  row: Pick<FixtureDividerRow, "dividersSetByHand" | "valid">,
): boolean => policy?.keepFixtureDividers === true && !row.dividersSetByHand && row.valid;

/**
 * Which shelves keep the dividers their fixture rows already have, as
 * `loaderShelfKey(bayNo, shelfNo)` keys. The rules modal and the store
 * request builders (`keepsFixtureDividers`) both decide through it.
 */
export const keptFixtureDividerShelves = ({
  policy,
  rows,
}: {
  policy: Pick<DividerPolicy, "keepFixtureDividers"> | null | undefined;
  rows: readonly FixtureDividerRow[];
}): Set<string> =>
  new Set(
    rows
      .filter((row) => fixtureRowKeepsDividers(policy, row))
      .map((row) => loaderShelfKey(row.bayNo, row.shelfNo)),
  );
